import { Terminal } from '@xterm/xterm';
import { Readline as ReadlineAddon } from 'xterm-readline';
import { compileRuntime, ElfJsRuntime, ElfRuntimeException } from '../../elf-core/ElfRuntime';
import { compileElfCode, TElfUserCompilationResult, TElfUserError, TElfUserWarning } from './ElfEditorTools';

export type TReplDefaults = {
	registers: number;
	stack: number;
	callStack: number;
	cpuCycles: number;
	heap: number;
};

export class ElfRepl {
	private terminal!: Terminal;
	private readline!: ReadlineAddon;
	constructor(public readonly envDefaults: TReplDefaults) {

	}

	setReadline(readline: ReadlineAddon) {
		this.readline = readline;
	}

	setTerminal(terminal: Terminal) {
		this.terminal = terminal;
	}

	scrollToBottom(): void {
		this.terminal.scrollToBottom();
	}

	// eslint-disable-next-line require-await
	async awaitDbgEval(line: number, column: number, isEval: boolean, callback: (input: string) => Promise<any>) {
		const red = '\x1b[31m';
		const yellow = '\x1b[33m';
		const brightGreen = '\x1b[0;92m';
		const reset = '\x1b[0m';
		const brightRed = '\x1b[91m';

		// repl should be in new line, if not add it!
		if (this.terminal.buffer.active.cursorX !== 0) {
			this.readline.write('\n');
		}

		return this.readline.read(`${red}elf-dbg:${line}:${column}${!isEval ? `${brightRed}[↓]${reset}` : `${brightGreen}[↑]${reset}`}${yellow}> ${reset}`).then(callback);
	}

	// eslint-disable-next-line require-await
	async awaitEval(callback: (input: string) => Promise<any>) {
		const green = '\x1b[32m';
		const yellow = '\x1b[33m';
		const reset = '\x1b[0m';

		// repl should be in new line, if not add it!
		if (this.terminal.buffer.active.cursorX !== 0) {
			this.readline.write('\n');
		}

		return this.readline.read(`${green}elf-eval${yellow}> ${reset}`).then(callback);
	}

	private printConsoleErrorMessage(normalizedInputLines: string[], errorPrefix: string, errorLine: Pick<TElfUserError | TElfUserWarning, 'line' | 'column' | 'message'>, ignoreErrorMessage?: boolean): void {
		const { line, column } = errorLine;
		const fullErrLine = normalizedInputLines[line - 1];

		if (!ignoreErrorMessage) {
			this.terminal.writeln(`${errorPrefix}${errorLine.message}`);
		}
		this.terminal.writeln(fullErrLine);
		const arrow = `${'\x1b[33m~\x1b[0m'.repeat(column - 1)}\x1b[33m^\x1b[0m`;

		this.terminal.writeln(`${arrow} @:${line}:${column}`);
	}

	prettyPrintCompileResults(code: string, compileResult: TElfUserCompilationResult) {
		const yellow = '\x1b[33m';
		const red = '\x1b[31m';
		const reset = '\x1b[0m';

		const normalizedInputLines = code.split('\n');

		for (const errorLine of compileResult.errors) {
			this.printConsoleErrorMessage(normalizedInputLines, `${red}Napaka:${reset} `, errorLine);
		}

		for (const errorLine of compileResult.warnings) {
			this.printConsoleErrorMessage(normalizedInputLines, `${yellow}Opozorilo:${reset} `, errorLine);
		}
	}

	writeUsage(runtimeArgs: Parameters<ElfJsRuntime['run']>[0]): void {
		const cs = runtimeArgs.callStack.getSize();
		const cc = runtimeArgs.cpuCycles.totalConsumed();
		const ss = runtimeArgs.stack.getSize();
		const ac = runtimeArgs.registers.getAccumulator();
		const magenta = '\x1b[95m';
		const reset = '\x1b[0m';

		this.terminal.writeln(`${magenta}{ accumulator:${ac}, cycles:${cc}, data_stack:${ss}, call_stack:${cs} }${reset}`);
	}

	async runElfJsRuntime(runtime: ElfJsRuntime, runtimeArgs: Parameters<ElfJsRuntime['run']>[0], opts?: { printSuccess?: boolean; elfCode?: string }): Promise<number | null> {
		const red = '\x1b[31m';
		const brightWhite = '\x1b[97m';
		const reset = '\x1b[0m';
		const printSuccess = opts?.printSuccess ?? false;
		const sourceCode = opts?.elfCode ?? null;

		let programStatus: Awaited<ReturnType<ElfJsRuntime['run']>> | null = null;

		try {
			const start = performance.now();

			programStatus = await runtime.run(runtimeArgs);
			const end = performance.now();
			const duration = end - start;

			if (programStatus.exitCode !== 0) {
				if (programStatus.userTerminated) {
					this.terminal.writeln(`${red}>>> Uporabnik je prekinil izvajanje${reset}`);
				} else {
					throw new ElfRuntimeException('Program terminiran z ne ničelno kodo napake!', `koda: ${programStatus.exitCode}`, programStatus.codeMap!);
				}
			} else if (printSuccess) {
				this.terminal.writeln(`${brightWhite}>>> Program uspešno zaključen${reset} (realen čas: ${duration.toFixed(3)} ms)`);
			}
		} catch (err: any) {
			if (err instanceof ElfRuntimeException) {
				const codePtr = err.codeMap ?? runtimeArgs.cpuCycles.getCodeMapPointer();
				const callStackMaxSize = 8;
				const callStackLastSize = 5;
				const callStack = runtimeArgs.callStack.getTailOf(callStackMaxSize);
				const prefixStack: typeof callStack = [];
				let idx = 0;

				while (callStack.length > callStackLastSize) {
					callStack.shift();
					prefixStack.push(runtimeArgs.callStack.peekAt(idx));
					idx++;
				}
				const prefixStackStr = prefixStack.length ? prefixStack.reverse().map((v) => `\t\t ↑ ${v.name} (:${v.codeMap.line}:${v.codeMap.column})`).join('\n') : '';
				const lastStackStr = runtimeArgs.callStack.getTailOf(callStackLastSize).reverse().map((v) => `\t\t ↑ ${v.name} (:${v.codeMap.line}:${v.codeMap.column})`).join('\n');
				const stackLine = prefixStackStr ? (`${lastStackStr}\n\t\t ...\n${prefixStackStr}`) : lastStackStr;

				this.terminal.writeln(`\n${red}Izjema ob izvajanju!${reset}`);
				this.terminal.writeln(`${err.message} (podrobnosti: ${err.detailedMessage || 'N/A'})\n\n\t\t 💥  (:${codePtr.line}:${codePtr.column})\n ${stackLine} \n\n`);
				if (sourceCode) {
					const normalizedInputLines = sourceCode.split('\n');

					this.printConsoleErrorMessage(normalizedInputLines, '', { ...codePtr, message: err.message }, true);
				}
			} else {
				this.terminal.writeln(`${red}Uf, nekaj je šlo narobe! 💣${reset}`);
				this.terminal.writeln(`${err.message}`);
			}
		}
		this.writeUsage(runtimeArgs);

		return programStatus?.exitCode ?? null;
	}

	write(str: string): void {
		this.terminal.write(str);
	}
	writeln(str: string): void {
		this.terminal.writeln(str);
	}
	writelnPromise(str: string): Promise<void> {
		return new Promise<void>((r) => this.terminal.writeln(str, r));
	}

	readlinePrompt(str: string): Promise<string> {
		if (this.terminal.buffer.active.cursorX !== 0) {
			this.readline.write('\n');
		}

		return this.readline.read(str);
	}

	async readlineCancellationPrompt(str: string, shouldCancel: () => boolean): Promise<string> {
		if (this.terminal.buffer.active.cursorX !== 0) {
			this.readline.write('\n');
		}
		const readPromise = this.readline.read(str);

		while (!shouldCancel()) {
			const result: string | -1 = await Promise.race([readPromise, new Promise<-1>((resolve) => setTimeout(() => resolve(-1), 100))]);

			if (result !== -1) return result;
		}

		return '';
	}

	writeAsUserCommand(str: string): void {
		const blue = '\x1b[34m';
		const cyan = '\x1b[36m';
		const reset = '\x1b[0m';

		this.terminal.writeln(
			`${cyan}elf@${cyan}polar${blue}:~${reset}$ ${str}`,
		);
	}

	async exec(code: string, runtimeArgs: Parameters<ElfJsRuntime['run']>[0], opts?: { asDebug?: boolean }): Promise<void> {
		const compileResult = compileElfCode(code, { registers: runtimeArgs.registers.getIndexedCount() });

		this.prettyPrintCompileResults(code, compileResult);
		if (!compileResult.compiled) {
			return;
		}
		const elfJsRuntime = compileRuntime(compileResult.ast.tree, compileResult.ast.treeCtx, { debug: opts?.asDebug ?? false });

		await this.runElfJsRuntime(elfJsRuntime, runtimeArgs);
	}
}
